import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  useNavigate,
  useSearchParams,
  useLocation,
} from 'hooks/use-router/use-router';
import {
  clientExpiryMilliseconds,
  redirectionTimeoutDuration,
  routesWithClientId,
  routes,
} from 'utils/constants';
import { redirectBackToClientHelper } from 'utils/helpers';
import {
  LocalStorageKeys,
  useLocalStorage,
  setLocalStorage,
} from 'utils/localStorage';
import { DatadogClient } from '@workivate/tho-web-shared';
const { CORRELATION_ID_KEY } = DatadogClient;

interface SvgIcons {
  [key: string]: SVGSVGElement['outerHTML'] | undefined | -1;
}

export type SessionContextType = {
  clientId?: string;
  correlationId?: string;
  redirectBackToClient: (error?: string) => void;
  username?: string; // needed by MFA Setup
  setUsername: (username?: string) => void; // set by Login
  showGenericError: boolean;
  setShowGenericError: (show: boolean) => void;
  svgIcons: SvgIcons;
  setSvgIcons: React.Dispatch<React.SetStateAction<SvgIcons>>;
  isLoginPersistent: boolean; // whether the user selected "keep me logged in" on the login page
  setIsLoginPersistent: (isLoginPersistent: boolean) => void;
};

export const SessionContext = createContext<SessionContextType>({
  clientId: undefined,
  correlationId: undefined,
  // setting redirectBackToClientHelper here allows testing of the expiry logic
  redirectBackToClient: (error) => redirectBackToClientHelper({ error }),
  username: undefined,
  setUsername: () => {},
  showGenericError: false,
  setShowGenericError: () => {},
  svgIcons: {},
  setSvgIcons: () => ({}),
  isLoginPersistent: false,
  setIsLoginPersistent: () => {},
});

export const useSessionContext = () => useContext(SessionContext);

type SessionProviderProps = {
  children: React.ReactNode;
};

export const SessionProvider = ({ children }: SessionProviderProps) => {
  const [username, setUsername] = useState<string | undefined>(undefined);
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const [correlationId, setCorrelationId] = useState<string | undefined>(
    undefined
  );
  const [isClientIdExpired, setIsClientIdExpired] = useState<boolean>(false);
  const [isLoginPersistent, setIsLoginPersistent] = useState<boolean>(false);
  const [showGenericError, setShowGenericError] = useState(false);
  const [svgIcons, setSvgIcons] = useState<SvgIcons>({});

  // if the user refreshes the page, we need to keep the original expiry time
  // otherwise the user could be stuck because the clientId has expired
  // but USL thinks it's still valid
  // so we use localStorage and check this at component mount
  const [storedClientId, setStoredClientId] = useLocalStorage<string>(
    LocalStorageKeys.clientId
  );
  const [storedClientIdExpiryDate, setStoredClientIdExpiryDate] =
    useLocalStorage<number>(LocalStorageKeys.clientIdExpiryDate);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const currentPath = location.pathname;

  const checkClientIdExpiry = (expiryTime: number) => {
    const timeRemaining = expiryTime - Date.now();

    if (timeRemaining <= 0) {
      setIsClientIdExpired(true);
    }

    if (timeRemaining <= -1 * redirectionTimeoutDuration) {
      // Show a help page in case the redirect doesn't work
      // Native apps sometimes don't do the redirect
      // or require user interaction with a popup that can be missed
      // delay is so that if the redirect works, the user doesn't see the help page
      // Only redirect if we're NOT already on the session-expired page to avoid endless refresh
      if (currentPath !== routes['/session-expired']) {
        navigate(routes['/session-expired'], {
          replace: true,
        });
      }
    }
  };

  useEffect(() => {
    // start checking the expiry of the client ID
    // we use setInterval insted of setTimeout in case the app is "backgrounded" or otherwise frozen
    // this can happen on Natives and a timeout can be missed
    // when the user returns to the app, the interval should catch expired client ID

    // Don't run the interval if we're already on the session-expired page
    if (currentPath === routes['/session-expired']) {
      return;
    }

    if (!clientId) return;

    let expiryTime = Date.now() + clientExpiryMilliseconds;

    // check if there is already an expiry date for the current clientId
    if (storedClientIdExpiryDate && storedClientId === clientId) {
      expiryTime = storedClientIdExpiryDate;
      const timeRemaining = storedClientIdExpiryDate - Date.now();

      if (timeRemaining <= 0) {
        setIsClientIdExpired(true); // client ID has expired
      }
    } else {
      // save in localStorage in case the page is refreshed
      setStoredClientId(clientId);
      setStoredClientIdExpiryDate(expiryTime);
    }

    const interval = setInterval(() => {
      checkClientIdExpiry(expiryTime);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [clientId, currentPath]);

  useEffect(() => {
    const clientIdParam = searchParams.get('client_id');
    if (clientId !== clientIdParam) {
      setClientId(clientIdParam || undefined);
      DatadogClient.setGlobalContextProperty('public_client_id', clientIdParam);
    }

    const correlationIdParam =
      searchParams.get(CORRELATION_ID_KEY) || undefined;
    if (correlationId !== correlationIdParam) {
      setCorrelationId(correlationIdParam);
    }

    const redirectToParam = searchParams.get('redirectTo');
    // A non-existent URL parameter can appear in different ways depending on
    // build tool, router etc. For example in NextJS it would be undefined
    // but after the migration to Vite, it would be null
    // The truthy check below means we only set the localStorage value when the URL has a non-falsy value
    if (redirectToParam) {
      setLocalStorage(LocalStorageKeys.redirectTo, redirectToParam);
    }
  }, [searchParams, clientId]);

  useEffect(() => {
    const currentRoute = location.pathname;

    if (
      (routesWithClientId as string[]).includes(currentRoute) &&
      isClientIdExpired
    ) {
      redirectBackToClientHelper({
        clientId,
        redirectUri: searchParams.get('redirect_uri') || undefined,
        searchParams,
        navigate,
      });
    }
  }, [isClientIdExpired, location.pathname, searchParams, navigate, clientId]);

  // memoise to prevent the object being recreated on every render
  const sessionContext = useMemo(
    () => ({
      clientId,
      correlationId,
      redirectBackToClient: (error?: string) => {
        redirectBackToClientHelper({
          clientId,
          error,
          redirectUri: searchParams.get('redirect_uri') || undefined,
          searchParams,
          navigate,
        });
      },
      username,
      setUsername,
      showGenericError,
      setShowGenericError,
      svgIcons,
      setSvgIcons,
      isLoginPersistent,
      setIsLoginPersistent,
    }),
    [
      clientId,
      correlationId,
      isLoginPersistent,
      username,
      showGenericError,
      svgIcons,
      searchParams,
      location.pathname,
      navigate,
    ]
  );

  return (
    <SessionContext.Provider value={sessionContext}>
      {children}
    </SessionContext.Provider>
  );
};
